import React, { useState, useEffect } from 'react';

interface DataFlow {
  from: string;
  to: string;
  data: string;
  active: boolean;
}

export default function FutrBetArchitecture() {
  const [activeFlow, setActiveFlow] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const flows: DataFlow[] = [
    { from: 'User', to: 'Frontend', data: 'Create Market / Place Bet', active: false },
    { from: 'Frontend', to: 'Worker API', data: 'POST /api/markets', active: false },
    { from: 'Worker API', to: 'D1 Database', data: 'INSERT INTO markets', active: false },
    { from: 'D1 Database', to: 'Worker API', data: 'Market Created', active: false },
    { from: 'Worker API', to: 'Frontend', data: 'Success Response', active: false },
    { from: 'Frontend', to: 'User', data: 'Update UI', active: false }
  ];

  useEffect(() => {
    if (!isAnimating) return;

    const interval = setInterval(() => {
      setActiveFlow((prev) => (prev + 1) % flows.length);
    }, 1000);

    return () => clearInterval(interval);
  }, [isAnimating, flows.length]);

  const components = [
    {
      id: 'user',
      name: 'User',
      icon: '👤',
      color: 'from-blue-500 to-blue-600',
      description: 'Creates markets, places bets',
      x: 50,
      y: 50
    },
    {
      id: 'frontend',
      name: 'React Frontend',
      icon: '⚛️',
      color: 'from-green-500 to-green-600',
      description: 'FutrBet UI (CloudFlare Pages)',
      x: 250,
      y: 50
    },
    {
      id: 'api',
      name: 'Worker API',
      icon: '⚡',
      color: 'from-orange-500 to-orange-600',
      description: 'Edge functions (CloudFlare Workers)',
      x: 450,
      y: 50
    },
    {
      id: 'd1',
      name: 'D1 Database',
      icon: '🗄️',
      color: 'from-purple-500 to-purple-600',
      description: 'SQLite at the edge',
      x: 450,
      y: 200
    },
    {
      id: 'r2',
      name: 'R2 Storage',
      icon: '💾',
      color: 'from-indigo-500 to-indigo-600',
      description: 'Image & file storage',
      x: 250,
      y: 200
    },
    {
      id: 'resolution',
      name: 'Resolution Oracle',
      icon: '🔮',
      color: 'from-pink-500 to-pink-600',
      description: 'Public API verification',
      x: 50,
      y: 200
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-green-400 to-orange-400 bg-clip-text text-transparent">
            FutrBet Architecture
          </h1>
          <p className="text-xl text-gray-400">
            Decentralized Prediction Markets on CloudFlare Edge
          </p>
        </div>

        {/* Control Panel */}
        <div className="mb-8 flex justify-center gap-4">
          <button
            onClick={() => setIsAnimating(!isAnimating)}
            className={`px-6 py-2 rounded-lg font-semibold transition-all ${
              isAnimating
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isAnimating ? '⏸ Pause' : '▶ Play'} Animation
          </button>
          <button
            onClick={() => setActiveFlow(0)}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-all"
          >
            🔄 Reset
          </button>
        </div>

        {/* Architecture Diagram */}
        <div className="relative bg-gray-900/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700 min-h-[500px]">
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
            {/* Connection Lines */}
            <line x1="150" y1="80" x2="250" y2="80" stroke="#4B5563" strokeWidth="2" strokeDasharray="5,5" />
            <line x1="350" y1="80" x2="450" y2="80" stroke="#4B5563" strokeWidth="2" strokeDasharray="5,5" />
            <line x1="500" y1="120" x2="500" y2="200" stroke="#4B5563" strokeWidth="2" strokeDasharray="5,5" />
            <line x1="450" y1="230" x2="350" y2="230" stroke="#4B5563" strokeWidth="2" strokeDasharray="5,5" />
            <line x1="250" y1="230" x2="150" y2="230" stroke="#4B5563" strokeWidth="2" strokeDasharray="5,5" />
            <line x1="100" y1="200" x2="100" y2="120" stroke="#4B5563" strokeWidth="2" strokeDasharray="5,5" />

            {/* Active Flow Animation */}
            {activeFlow === 0 && <circle cx="200" cy="80" r="5" fill="#10B981">
              <animate attributeName="cx" from="150" to="250" dur="1s" repeatCount="1" />
            </circle>}
            {activeFlow === 1 && <circle cx="400" cy="80" r="5" fill="#10B981">
              <animate attributeName="cx" from="350" to="450" dur="1s" repeatCount="1" />
            </circle>}
            {activeFlow === 2 && <circle cx="500" cy="160" r="5" fill="#10B981">
              <animate attributeName="cy" from="120" to="200" dur="1s" repeatCount="1" />
            </circle>}
            {activeFlow === 3 && <circle cx="500" cy="160" r="5" fill="#F97316">
              <animate attributeName="cy" from="200" to="120" dur="1s" repeatCount="1" />
            </circle>}
            {activeFlow === 4 && <circle cx="400" cy="80" r="5" fill="#F97316">
              <animate attributeName="cx" from="450" to="350" dur="1s" repeatCount="1" />
            </circle>}
            {activeFlow === 5 && <circle cx="200" cy="80" r="5" fill="#F97316">
              <animate attributeName="cx" from="250" to="150" dur="1s" repeatCount="1" />
            </circle>}
          </svg>

          {/* Components */}
          {components.map((comp) => (
            <div
              key={comp.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
              style={{ left: `${comp.x}px`, top: `${comp.y}px` }}
            >
              <div
                className={`bg-gradient-to-r ${comp.color} p-6 rounded-2xl shadow-2xl hover:scale-105 transition-all cursor-pointer group`}
              >
                <div className="text-4xl mb-2 text-center">{comp.icon}</div>
                <h3 className="font-bold text-sm text-center mb-1">{comp.name}</h3>
                <p className="text-xs text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity text-center max-w-[150px]">
                  {comp.description}
                </p>
              </div>
            </div>
          ))}

          {/* Flow Status */}
          <div className="absolute bottom-4 left-4 right-4 bg-black/50 rounded-lg p-4 z-20">
            <div className="text-sm font-mono text-green-400">
              Current Flow: {flows[activeFlow]?.data || 'Idle'}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {flows[activeFlow]?.from} → {flows[activeFlow]?.to}
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-4 text-green-400">Frontend Layer</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• React 18 with TypeScript</li>
              <li>• CloudFlare Pages hosting</li>
              <li>• Tailwind CSS styling</li>
              <li>• Real-time state management</li>
            </ul>
          </div>

          <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-4 text-orange-400">API Layer</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• CloudFlare Workers</li>
              <li>• Edge computing</li>
              <li>• RESTful endpoints</li>
              <li>• Global distribution</li>
            </ul>
          </div>

          <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-4 text-purple-400">Data Layer</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• D1 SQLite database</li>
              <li>• R2 object storage</li>
              <li>• Edge caching</li>
              <li>• ACID compliance</li>
            </ul>
          </div>
        </div>

        {/* API Endpoints */}
        <div className="mt-12 bg-gray-800/50 backdrop-blur rounded-xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold mb-6 text-center">API Endpoints</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold text-green-400 mb-3">Market Operations</h3>
              <div className="space-y-2 font-mono text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-400">GET</span>
                  <span className="text-gray-400">/api/markets</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-400">POST</span>
                  <span className="text-gray-400">/api/markets</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-400">GET</span>
                  <span className="text-gray-400">/api/markets/:slug</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-400">PUT</span>
                  <span className="text-gray-400">/api/markets/:id/resolve</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-orange-400 mb-3">Betting & Users</h3>
              <div className="space-y-2 font-mono text-sm">
                <div className="flex justify-between">
                  <span className="text-green-400">POST</span>
                  <span className="text-gray-400">/api/bets</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-400">GET</span>
                  <span className="text-gray-400">/api/users/:id/bets</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-400">GET</span>
                  <span className="text-gray-400">/api/leaderboard</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-400">POST</span>
                  <span className="text-gray-400">/api/users</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resolution System */}
        <div className="mt-12 bg-gradient-to-r from-green-900/30 to-orange-900/30 rounded-xl p-8 border border-green-500/30">
          <h2 className="text-2xl font-bold mb-6 text-center">Public Resolution System</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-3">📰</div>
              <h3 className="font-bold mb-2">News APIs</h3>
              <p className="text-sm text-gray-400">
                Reuters, AP, Bloomberg for financial markets
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">📊</div>
              <h3 className="font-bold mb-2">Data Oracles</h3>
              <p className="text-sm text-gray-400">
                CoinGecko, Yahoo Finance, Sports APIs
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">👥</div>
              <h3 className="font-bold mb-2">Community Consensus</h3>
              <p className="text-sm text-gray-400">
                Dispute resolution via voting
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}